import React, { useState, useEffect } from 'react';
import { FileText, Download, Plus, Trash2, Save, Eye, AlertCircle, Building2, User, X } from 'lucide-react';
import pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import ReactGA from 'react-ga4';

pdfMake.vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfFonts;

// Initialize Google Analytics
const TRACKING_ID = 'G-DWB7K02L50'; // ⚠️ ÎNLOCUIEȘTE CU ID-UL TĂU DE LA GOOGLE ANALYTICS
ReactGA.initialize(TRACKING_ID);

const App = () => {
  const [showApp, setShowApp] = useState(false);
  const [step, setStep] = useState(1);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  
  const [company, setCompany] = useState({
    name: '',
    cui: '',
    regCom: '',
    address: '',
    city: '',
    county: '',
    phone: '',
    email: '',
    bank: '',
    iban: ''
  });

  const [client, setClient] = useState({
  type: 'company', // 'company' sau 'individual'
  name: '',
  cui: '', // sau CNP pentru persoană fizică
  address: '',
  city: '',
  county: ''
});

  const [invoice, setInvoice] = useState({
    number: '',
	series: 'FACT',
	date: new Date().toISOString().split('T')[0],
	dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  });

  const [items, setItems] = useState([
    { description: '', quantity: 1, price: 0, unit: 'buc' }
  ]);
  
  const [cuiSearch, setCuiSearch] = useState('');
  const [loadingANAF, setLoadingANAF] = useState(false);
  const [anafError, setAnafError] = useState('');

  useEffect(() => {
    // Track page view
    ReactGA.send({ hitType: "pageview", page: window.location.pathname });

    // Load saved company data - FIX: unified to pdfFactura_
    const saved = localStorage.getItem('pdfFactura_company');
    if (saved) {
      setCompany(JSON.parse(saved));
    }
    
    // Check cookie consent - FIX: unified to pdfFactura_
    const cookieConsent = localStorage.getItem('pdfFactura_cookieConsent');
    if (!cookieConsent) {
      setTimeout(() => setShowCookieBanner(true), 100);
    }
  }, []);

  const acceptCookies = () => {
    localStorage.setItem('pdfFactura_cookieConsent', 'accepted');
    setShowCookieBanner(false);
    
    // Track cookie acceptance
    ReactGA.event({
      category: 'User',
      action: 'Accept Cookies'
    });
  };

  const showSuccess = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 3000);
  };

  const showError = (msg) => {
    setError(msg);
    setTimeout(() => setError(''), 4000);
  };

  const addItem = () => {
    setItems([...items, { description: '', quantity: 1, price: 0, unit: 'buc' }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const updateItem = (index, field, value) => {
    const updated = [...items];
    updated[index][field] = value;
    setItems(updated);
  };

  const calculateSubtotal = () => {
    return items.reduce((sum, item) => sum + (item.quantity * item.price), 0);
  };

  const calculateVAT = () => {
    return calculateSubtotal() * 0.21;
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateVAT();
  };

  const validateStep = (currentStep) => {
    if (currentStep === 1) {
      if (!company.name || !company.cui) {
        showError('Completează numele firmei și CUI');
        return false;
      }
    } else if (currentStep === 2) {
      if (!client.name) {
        showError('Completează numele clientului');
        return false;
      }
    } else if (currentStep === 3) {
      if (!invoice.number || !invoice.series) {
        showError('Completează seria și numărul facturii');
        return false;
      }
      if (items.some(item => !item.description || item.price <= 0)) {
        showError('Completează toate produsele/serviciile');
        return false;
      }
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep(step)) {
      if (step === 1) {
        localStorage.setItem('pdfFactura_company', JSON.stringify(company));
      }
      
      // Track step progression
      ReactGA.event({
        category: 'Form',
        action: 'Next Step',
        label: `Step ${step} to ${step + 1}`
      });
      
      setStep(step + 1);
    }
  };

  const resetForm = () => {
  setStep(1);
  setClient({ type: 'company', name: '', cui: '', address: '', city: '', county: '' }); // ⬅️ Adaugă type
  setInvoice({
    number: '',
	series: 'FACT',
	date: new Date().toISOString().split('T')[0],
	dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  });
  setItems([{ description: '', quantity: 1, price: 0, unit: 'buc' }]);
  showSuccess('Gata pentru o factură nouă!');
  
  ReactGA.event({
    category: 'Form',
    action: 'Reset Form'
  });
};

// Helper: extrage orașul din adresa ANAF
const parseCity = (address) => {
  if (!address) return '';
  
  // Pattern: "BUCURESTI, SECTOR 3" sau "CLUJ-NAPOCA, STR..."
  const cityMatch = address.match(/^([A-ZĂÎÂȘȚ\-\s]+)(?:,|\s+STR)/i);
  if (cityMatch) {
    return cityMatch[1].trim();
  }
  
  // Fallback: primele 1-2 cuvinte
  const words = address.split(/[,\s]+/).filter(w => w.length > 2);
  return words.slice(0, 2).join(' ');
};

// Fetch company data din ANAF
const fetchCompanyData = async () => {
  if (!cuiSearch.trim()) {
    setAnafError('Introdu un CUI');
    return;
  }

  setLoadingANAF(true);
  setAnafError('');

  try {
    const response = await fetch('/api/anaf-cui', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ cui: cuiSearch })
    });

    const data = await response.json();

    if (data.success) {
      // Auto-fill câmpurile client
      setClient({
        ...client,
        name: data.data.name || '',
        cui: `RO${data.data.cui}`,
        address: data.data.address || '',
        city: parseCity(data.data.address) || '',
        county: '' // ANAF nu oferă județ separat
      });

      showSuccess('✅ Date completate automat din ANAF!');
      setCuiSearch(''); // Clear search field
      
      // Track ANAF autocomplete success
      ReactGA.event({
        category: 'ANAF',
        action: 'Autocomplete Success',
        label: cuiSearch
      });
    } else {
      setAnafError(data.error || 'CUI nu a fost găsit');
      
      // Track ANAF autocomplete failure
      ReactGA.event({
        category: 'ANAF',
        action: 'Autocomplete Failed',
        label: cuiSearch
      });
    }
  } catch (error) {
    setAnafError('Eroare la conectarea cu ANAF. Completează manual.');
    console.error('ANAF fetch error:', error);
    
    ReactGA.event({
      category: 'ANAF',
      action: 'Autocomplete Error',
      label: error.message
    });
  } finally {
    setLoadingANAF(false);
  }
};

  const checkAndIncrementUsage = () => {
  const usage = JSON.parse(localStorage.getItem('pdfFactura_usage') || '{"count": 0, "lastDate": ""}');
  //const today = new Date().toISOString().split('T')[0];
  const today = '2025-12-31'; // ⬅️ Ultima zi din 2025 (sigur acceptată de ANAF)
  console.log(`[ANAF API] Using fixed date: ${today} (ANAF doesn't accept 2026 yet)`);
  
  // Reset counter la miezul nopții
  if (usage.lastDate !== today) {
    usage.count = 0;
    usage.lastDate = today;
  }
  
  // Verifică limita
  if (usage.count >= 100) {
    showError('Ai atins limita de 100 facturi/zi.');
    
    // Track limit reached
    ReactGA.event({
      category: 'Limit',
      action: 'Daily Limit Reached',
      value: 100
    });
    
    return false;
  }
  
  // Incrementează counter
  usage.count += 1;
  localStorage.setItem('pdfFactura_usage', JSON.stringify(usage));
  
  return true;
};

  const createDocDefinition = () => {
	const clientIdLabel = client.type === 'individual' ? 'CNP' : 'CUI';
    return {
      pageSize: 'A4',
      pageMargins: [40, 60, 40, 60],
      content: [
        {
          text: 'FACTURĂ',
          style: 'header',
          alignment: 'center',
          margin: [0, 0, 0, 10]
        },
        {
          columns: [
    {
      width: '50%',
      text: [
        { text: 'Serie: ', bold: true },
        invoice.series,
        { text: '  Nr: ', bold: true },
        invoice.number
      ]
    },
    {
      width: '50%',
      stack: [
        {
          text: [
            { text: 'Data: ', bold: true },
            invoice.date
          ],
          alignment: 'right'
        },
        {
          text: [
            { text: 'Scadență: ', bold: true },
            invoice.dueDate
          ],
          alignment: 'right',
          margin: [0, 3, 0, 0]
        }
      ]
    }
  ],
  margin: [0, 0, 0, 20]
        },
        {
          columns: [
            {
              width: '50%',
              stack: [
                { text: 'FURNIZOR', style: 'subheader', margin: [0, 0, 0, 5] },
                { text: company.name, bold: true },
                { text: `CUI: ${company.cui}` },
                company.regCom ? { text: `Reg. Com.: ${company.regCom}` } : {},
                { text: company.address },
                { text: `${company.city}${company.county ? ', jud. ' + company.county : ''}` },
                company.phone ? { text: `Tel: ${company.phone}` } : {},
                company.email ? { text: `Email: ${company.email}` } : {},
                company.bank ? { text: `Banca: ${company.bank}` } : {},
                company.iban ? { text: `IBAN: ${company.iban}` } : {}
              ]
            },
            {
              width: '50%',
              stack: [
                { text: 'CLIENT', style: 'subheader', margin: [0, 0, 0, 5] },
                { text: client.name, bold: true },
                client.cui ? { text: `${clientIdLabel}: ${client.cui}` } : {},
                { text: client.address },
                { text: `${client.city}${client.county ? ', jud. ' + client.county : ''}` }
              ]
            }
          ],
          margin: [0, 0, 0, 20]
        },
        {
          table: {
            headerRows: 1,
            widths: ['*', 40, 50, 60, 70],
            body: [
              [
                { text: 'Denumire produs/serviciu', style: 'tableHeader' },
                { text: 'UM', style: 'tableHeader', alignment: 'center' },
                { text: 'Cant.', style: 'tableHeader', alignment: 'center' },
                { text: 'Preț unitar', style: 'tableHeader', alignment: 'right' },
                { text: 'Valoare', style: 'tableHeader', alignment: 'right' }
              ],
              ...items.map(item => [
                item.description,
                { text: item.unit, alignment: 'center' },
                { text: item.quantity.toString(), alignment: 'center' },
                { text: item.price.toFixed(2) + ' RON', alignment: 'right' },
                { text: (item.quantity * item.price).toFixed(2) + ' RON', alignment: 'right' }
              ])
            ]
          },
          layout: {
            fillColor: (rowIndex) => rowIndex === 0 ? '#e5e7eb' : null,
            hLineWidth: () => 0.5,
            vLineWidth: () => 0.5,
            hLineColor: () => '#d1d5db',
            vLineColor: () => '#d1d5db'
          }
        },
        {
          columns: [
            { width: '*', text: '' },
            {
              width: 200,
              table: {
                widths: ['*', 70],
                body: [
                  [
                    { text: 'Subtotal:', alignment: 'right', border: [false, false, false, false] },
                    { text: calculateSubtotal().toFixed(2) + ' RON', alignment: 'right', border: [false, false, false, false] }
                  ],
                  [
                    { text: 'TVA (21%):', alignment: 'right', border: [false, false, false, false] },
                    { text: calculateVAT().toFixed(2) + ' RON', alignment: 'right', border: [false, false, false, false] }
                  ],
                  [
                    { text: 'TOTAL:', bold: true, alignment: 'right', border: [false, true, false, false] },
                    { text: calculateTotal().toFixed(2) + ' RON', bold: true, alignment: 'right', border: [false, true, false, false] }
                  ]
                ]
              },
              margin: [0, 10, 0, 0]
            }
          ]
        },
        {
          stack: [
    {
      text: 'Generated with PDF Factura',
      fontSize: 8,
      color: '#9ca3af',
      alignment: 'center'
    },
    {
      text: 'pdffactura.org',
      fontSize: 8,
      color: '#9ca3af',
      alignment: 'center',
      margin: [0, 2, 0, 0]
    }
  ],
  margin: [0, 30, 0, 0]
        }
      ],
      styles: {
        header: {
          fontSize: 22,
          bold: true
        },
        subheader: {
          fontSize: 12,
          bold: true,
          color: '#374151'
        },
        tableHeader: {
          bold: true,
          fontSize: 10,
          color: '#1f2937'
        }
      },
      defaultStyle: {
        fontSize: 9
      }
    };
  };

  const generatePDF = () => {
    if (!validateStep(3)) return;
    if (!checkAndIncrementUsage()) return;

    const docDefinition = createDocDefinition();
    pdfMake.createPdf(docDefinition).download(`Factura_${invoice.series}_${invoice.number}.pdf`);
    showSuccess('PDF generat cu succes!');
    
    // Track PDF generation
    ReactGA.event({
      category: 'PDF',
      action: 'Download',
      label: `${invoice.series}-${invoice.number}`,
      value: Math.round(calculateTotal())
    });
  };

  const previewPDF = () => {
    if (!validateStep(3)) return;
    if (!checkAndIncrementUsage()) return;

    const docDefinition = createDocDefinition();
    pdfMake.createPdf(docDefinition).open();
    showSuccess('Preview deschis în tab nou!');
    
    // Track PDF preview
    ReactGA.event({
      category: 'PDF',
      action: 'Preview',
      label: `${invoice.series}-${invoice.number}`
    });
  };

  const CookieBanner = () => (
    showCookieBanner && (
      <div className="fixed bottom-0 left-0 right-0 bg-gray-900 text-white p-6 shadow-2xl z-50">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex-1">
            <p className="text-sm">
              Folosim cookies și tehnologii similare pentru a îmbunătăți experiența ta și pentru a afișa reclame. 
              Continuând, ești de acord cu{' '}
              <button onClick={() => { setShowCookieBanner(false); setShowPrivacy(true); }} className="underline hover:text-blue-300">
                Politica de Confidențialitate
              </button>.
            </p>
          </div>
          <button
            onClick={acceptCookies}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg font-medium whitespace-nowrap transition"
          >
            Accept
          </button>
        </div>
      </div>
    )
  );

  const TermsModal = () => (
    showTerms && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl my-8">
          <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900">Termeni și Condiții</h2>
            <button onClick={() => setShowTerms(false)} className="text-gray-500 hover:text-gray-700">
              <X size={24} />
            </button>
          </div>
          
          <div className="p-6 space-y-6 text-gray-700 max-h-[60vh] overflow-y-auto">
            <p className="text-sm text-gray-500">Ultima actualizare: 25 ianuarie 2025</p>

            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">1. Acceptarea Termenilor</h3>
              <p>
                Prin accesarea și utilizarea PDF Factura ("Serviciul"), ești de acord să fii legat de acești Termeni și Condiții. 
                Dacă nu ești de acord, te rugăm să nu folosești Serviciul.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">2. Descrierea Serviciului</h3>
              <p>
                PDF Factura este un instrument online care te ajută să generezi documente PDF în format factură. 
                Serviciul calculează automat TVA și formatează datele introduse de tine într-un document PDF descărcabil.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">3. Utilizare "AS-IS"</h3>
              <p className="mb-2">
                Serviciul este furnizat "în forma actuală" și "după cum este disponibil", fără garanții de niciun fel, 
                exprese sau implicite, inclusiv dar fără a se limita la:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Garanții de comercializare</li>
                <li>Garanții de conformitate cu legislația fiscală</li>
                <li>Garanții de acuratețe sau rezultate specifice</li>
                <li>Garanții de funcționare neîntreruptă</li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">4. Responsabilitatea Utilizatorului</h3>
              <p className="mb-2">Utilizatorul este exclusiv responsabil pentru:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Acuratețea tuturor datelor introduse în sistem</li>
                <li>Verificarea conformității documentelor generate cu legislația în vigoare</li>
                <li>Consultarea unui contabil autorizat pentru aspecte fiscale specifice</li>
                <li>Respectarea termenelor și obligațiilor fiscale</li>
                <li>Orice consecințe rezultate din utilizarea documentelor generate</li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">5. Limitarea Răspunderii</h3>
              <p className="mb-2">
                PDF Factura și operatorii săi NU își asumă răspunderea și nu vor fi răspunzători pentru:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Erori în calcule sau date introduse de utilizator</li>
                <li>Modificări ale legislației fiscale sau contabile</li>
                <li>Respingeri ale documentelor de către ANAF sau alte autorități</li>
                <li>Pierderi financiare, penalități sau daune de orice natură</li>
                <li>Întreruperi ale serviciului sau pierderi de date</li>
                <li>Probleme tehnice sau erori de sistem</li>
              </ul>
              <p className="mt-3">
                În niciun caz răspunderea noastră nu va depăși suma plătită de tine pentru serviciu în ultimele 12 luni.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">6. Consultanță Profesională</h3>
              <p>
                PDF Factura NU oferă consultanță fiscală, contabilă sau juridică. Documentele generate sunt instrumente 
                de formatare și calcul, NU sfaturi profesionale. Pentru aspecte specifice legate de obligațiile tale fiscale, 
                consultă un contabil autorizat sau un consilier fiscal.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">7. Publicitate și Reclame</h3>
              <p>
                Serviciul poate afișa reclame terțe (inclusiv Google AdSense). Nu controlăm conținutul acestor reclame 
                și nu ne asumăm răspunderea pentru produsele sau serviciile promovate prin acestea.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">8. Limitări de Utilizare</h3>
              <p>
                Versiunea gratuită permite generarea a 100 facturi pe zi. Ne rezervăm dreptul de a modifica aceste limite 
                sau de a întrerupe serviciul în orice moment, fără preaviz.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">9. Modificări ale Termenilor</h3>
              <p>
                Ne rezervăm dreptul de a modifica acești termeni în orice moment. Continuarea utilizării serviciului 
                după modificări constituie acceptarea noilor termeni.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">10. Legea Aplicabilă</h3>
              <p>
                Acești termeni sunt guvernați de legile României. Orice litigiu va fi soluționat de instanțele competente din România.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">11. Contact</h3>
              <p>
                Pentru întrebări despre acești termeni, ne poți contacta la: contact@pdffactura.org
              </p>
            </section>
          </div>

          <div className="sticky bottom-0 bg-gray-50 border-t p-6">
            <button
              onClick={() => setShowTerms(false)}
              className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
            >
              Am înțeles
            </button>
          </div>
        </div>
      </div>
    )
  );

  const PrivacyModal = () => (
    showPrivacy && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl my-8">
          <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900">Politică de Confidențialitate</h2>
            <button onClick={() => setShowPrivacy(false)} className="text-gray-500 hover:text-gray-700">
              <X size={24} />
            </button>
          </div>
          
          <div className="p-6 space-y-6 text-gray-700 max-h-[60vh] overflow-y-auto">
            <p className="text-sm text-gray-500">Ultima actualizare: 25 ianuarie 2025</p>
            
            <p>
              Această Politică de Confidențialitate descrie cum PDF Factura colectează, folosește și protejează 
              informațiile tale personale în conformitate cu Regulamentul General privind Protecția Datelor (GDPR).
            </p>

            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">1. Operator de Date</h3>
              <p>
                Pentru întrebări legate de prelucrarea datelor personale, ne poți contacta la:<br />
                Email: contact@pdffactura.org
              </p>
            </section>

            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">2. Ce Date Colectăm</h3>
              <p className="mb-2">Colectăm următoarele tipuri de informații:</p>
              
              <h4 className="font-bold mt-4 mb-2">A. Date introduse de tine:</h4>
              <ul className="list-disc pl-6 space-y-1">
                <li>Date firmă: nume firmă, CUI, număr registru comerț, adresă, telefon, email, bancă, IBAN</li>
                <li>Date client: nume, CUI/CNP, adresă</li>
                <li>Date facturi: serie, număr, produse/servicii, cantități, prețuri</li>
              </ul>

              <h4 className="font-bold mt-4 mb-2">B. Date de utilizare:</h4>
              <ul className="list-disc pl-6 space-y-1">
                <li>Numărul de facturi generate</li>
                <li>Data ultimei utilizări</li>
              </ul>

              <h4 className="font-bold mt-4 mb-2">C. Cookies și tehnologii similare:</h4>
              <ul className="list-disc pl-6 space-y-1">
                <li>Cookie-uri de funcționare (localStorage pentru salvare date)</li>
                <li>Cookie-uri analytics (Google Analytics - opțional)</li>
                <li>Cookie-uri publicitare (Google AdSense)</li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">3. Cum Folosim Datele</h3>
              <p className="mb-2">Folosim datele colectate pentru:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Generarea documentelor PDF solicitate de tine</li>
                <li>Salvarea datelor firmei tale pentru confort (localStorage)</li>
                <li>Limitarea utilizării la 100 facturi/zi</li>
                <li>Îmbunătățirea serviciului nostru</li>
                <li>Afișarea de reclame relevante (prin Google AdSense)</li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">4. Unde Sunt Stocate Datele</h3>
              <p className="mb-2">
                <strong>IMPORTANT:</strong> Datele tale sunt stocate LOCAL în browser-ul tău (localStorage), 
                NU pe serverele noastre. Aceasta înseamnă:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>✓ Datele rămân pe dispozitivul tău</li>
                <li>✓ Noi nu avem acces la datele tale personale</li>
                <li>✓ Datele nu sunt trimise pe niciun server</li>
                <li>✗ Dacă ștergi istoricul browser-ului, pierzi datele salvate</li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">5. Partajarea cu Terți</h3>
              <p className="mb-2">Nu vindem și nu partajăm datele tale personale. Totuși, folosim servicii terțe:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Google Analytics:</strong> Pentru statistici anonime de utilizare (opțional)</li>
                <li><strong>Google AdSense:</strong> Pentru afișarea reclamelor (poate colecta date pentru targetare)</li>
              </ul>
              <p className="mt-2">
                Aceste servicii terțe au propriile politici de confidențialitate și folosesc cookie-uri.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">6. Cât Timp Păstrăm Datele</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>Date în localStorage: Până când ștergi istoricul browser-ului sau manual</li>
                <li>Cookie-uri: Conform setărilor tale de browser</li>
                <li>Analytics: Date anonimizate, 26 luni (Google Analytics standard)</li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">7. Drepturile Tale (GDPR)</h3>
              <p className="mb-2">Conform GDPR, ai următoarele drepturi:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Dreptul de acces:</strong> Poți vedea ce date sunt stocate (în browser DevTools → Application → localStorage)</li>
                <li><strong>Dreptul la ștergere:</strong> Poți șterge datele oricând (Clear localStorage sau șterge istoric browser)</li>
                <li><strong>Dreptul la portabilitate:</strong> Datele sunt în browser-ul tău, le poți exporta manual</li>
                <li><strong>Dreptul la opoziție:</strong> Poți refuza cookie-urile terțe în banner-ul GDPR</li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">8. Cum Să Ștergi Datele</h3>
              <p className="mb-2">Pentru a șterge complet datele tale:</p>
              <ol className="list-decimal pl-6 space-y-1">
                <li>Șterge istoricul și datele de navigare din browser</li>
                <li>SAU: Deschide DevTools (F12) → Application → localStorage → Șterge toate</li>
                <li>Pentru cookies publicitare: Setări browser → Cookie-uri → Șterge</li>
              </ol>
            </section>

            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">9. Securitate</h3>
              <p>
                Deoarece datele sunt stocate local în browser-ul tău, securitatea lor depinde de securitatea 
                dispozitivului tău. Recomandăm să folosești parole puternice și să nu partajezi dispozitivul cu persoane neautorizate.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">10. Cookie-uri</h3>
              <p className="mb-2">Folosim următoarele tipuri de cookie-uri:</p>
              
              <h4 className="font-bold mt-3 mb-2">Esențiale (nu pot fi dezactivate):</h4>
              <ul className="list-disc pl-6 space-y-1">
                <li>localStorage - salvare date firmă și contorizare facturi</li>
              </ul>

              <h4 className="font-bold mt-3 mb-2">Opționale (poți refuza):</h4>
              <ul className="list-disc pl-6 space-y-1">
                <li>Google Analytics - statistici anonime</li>
                <li>Google AdSense - afișare reclame personalizate</li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">11. Modificări ale Politicii</h3>
              <p>
                Ne rezervăm dreptul de a actualiza această politică. Vom posta versiunea actualizată pe această pagină 
                cu data modificării.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">12. Contact și Reclamații</h3>
              <p>
                Pentru întrebări despre această politică sau pentru a-ți exercita drepturile GDPR:<br />
                Email: contact@pdffactura.org
              </p>
              <p className="mt-2">
                Ai dreptul să depui o plângere la Autoritatea Națională de Supraveghere a Prelucrării Datelor cu Caracter Personal (ANSPDCP).
              </p>
            </section>
          </div>

          <div className="sticky bottom-0 bg-gray-50 border-t p-6">
            <button
              onClick={() => setShowPrivacy(false)}
              className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
            >
              Am înțeles
            </button>
          </div>
        </div>
      </div>
    )
  );

  if (!showApp) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
        <header className="bg-white shadow-sm border-b">
          <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="text-blue-600" size={32} />
              <h1 className="text-2xl font-bold text-gray-800">PDF Factura</h1>
            </div>
            <button
              onClick={() => {
                setShowApp(true);
                ReactGA.event({
                  category: 'Navigation',
                  action: 'Start App',
                  label: 'Header Button'
                });
              }}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
            >
              Generează Gratis
            </button>
          </div>
        </header>

        <section className="max-w-6xl mx-auto px-4 py-16 text-center">
          <h2 className="text-5xl font-bold text-gray-900 mb-4">
            Facturi în 2 minute
          </h2>
          <p className="text-2xl text-gray-600 mb-8">
            Fără cont. Fără complicații.
          </p>
          
          <button
            onClick={() => {
              setShowApp(true);
              ReactGA.event({
                category: 'Navigation',
                action: 'Start App',
                label: 'Hero CTA'
              });
            }}
            className="px-12 py-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition font-bold text-xl shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            Generează Gratis Acum
          </button>

          <div className="flex justify-center gap-8 mt-8 text-gray-700">
            <div className="flex items-center gap-2">
              <span className="text-green-600 text-2xl">✓</span>
              <span>100 facturi gratuite/zi</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-green-600 text-2xl">✓</span>
              <span>Fără înregistrare</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-green-600 text-2xl">✓</span>
              <span>Format adaptat pentru România</span>
            </div>
          </div>
        </section>

        <section className="bg-white py-16">
          <div className="max-w-6xl mx-auto px-4">
            <h3 className="text-3xl font-bold text-center text-gray-900 mb-12">
              Cum funcționează? 3 pași simpli
            </h3>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Building2 className="text-blue-600" size={32} />
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">1. Datele tale</h4>
                <p className="text-gray-600">
                  Completezi o singură dată informațiile firmei tale
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <User className="text-green-600" size={32} />
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">2. Adaugi clientul</h4>
                <p className="text-gray-600">
                  Introduci rapid datele clientului și produsele/serviciile
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Download className="text-purple-600" size={32} />
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">3. Descarci PDF</h4>
                <p className="text-gray-600">
                  Preview și descarcă factura instant
                </p>
              </div>
            </div>

            <p className="text-center text-2xl font-semibold text-gray-900 mt-12">
              2 minute. Atât. ⚡
            </p>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-4 py-16">
          <h3 className="text-3xl font-bold text-center text-gray-900 mb-12">
            De ce PDF Factura?
          </h3>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="text-4xl mb-4">⚡</div>
              <h4 className="text-2xl font-bold text-gray-900 mb-3">Cel mai rapid</h4>
              <p className="text-gray-600 text-lg">
                Generezi în 2 minute, nu 20. Fără instalări, fără configurări complexe.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="text-4xl mb-4">🇷🇴</div>
              <h4 className="text-2xl font-bold text-gray-900 mb-3">Optimizat România</h4>
              <p className="text-gray-600 text-lg">
                TVA 21%, câmpuri specifice României, serie și număr factură.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="text-4xl mb-4">🎯</div>
              <h4 className="text-2xl font-bold text-gray-900 mb-3">Zero complicații</h4>
              <p className="text-gray-600 text-lg">
                Fără cont, fără instalări, fără abonamente obligatorii. Doar generezi și descarci.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="text-4xl mb-4">💼</div>
              <h4 className="text-2xl font-bold text-gray-900 mb-3">Pentru freelanceri</h4>
              <p className="text-gray-600 text-lg">
                Perfect pentru PFA, freelanceri și micro-întreprinderi. Simplu si Rapid.
              </p>
            </div>
          </div>
        </section>

        <section className="bg-white py-16">
          <div className="max-w-4xl mx-auto px-4">
            <h3 className="text-3xl font-bold text-center text-gray-900 mb-12">
              Întrebări frecvente
            </h3>

            <div className="space-y-6">
              <div className="border-b pb-6">
                <h4 className="text-xl font-semibold text-gray-900 mb-2">
                  ❓ Costă ceva?
                </h4>
                <p className="text-gray-600 text-lg">
                  Nu, poți genera 100 facturi pe zi complet gratuit.
                </p>
              </div>

              <div className="border-b pb-6">
                <h4 className="text-xl font-semibold text-gray-900 mb-2">
                  ❓ Trebuie să mă înregistrez?
                </h4>
                <p className="text-gray-600 text-lg">
                  Nu, generezi instant fără cont. Datele firmei tale sunt salvate local 
                  în browser pentru confort.
                </p>
              </div>

              <div className="border-b pb-6">
                <h4 className="text-xl font-semibold text-gray-900 mb-2">
                  ❓ Ce conține factura?
                </h4>
                <p className="text-gray-600 text-lg">
                  Serie și număr factură, date furnizor și client, produse/servicii, 
                  cantități, prețuri, TVA 21% calculat automat, total.
                </p>
              </div>

              <div className="pb-6">
                <h4 className="text-xl font-semibold text-gray-900 mb-2">
                  ❓ Trebuie să verific ceva?
                </h4>
                <p className="text-gray-600 text-lg">
                  Da, recomandăm să verifici toate datele înainte de a trimite factura. 
                  Tu ești responsabil pentru acuratețea informațiilor introduse.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 py-16 text-center">
          <h3 className="text-4xl font-bold text-gray-900 mb-6">
            Gata să generezi prima factură?
          </h3>
          
          <button
            onClick={() => {
              setShowApp(true);
              ReactGA.event({
                category: 'Navigation',
                action: 'Start App',
                label: 'Bottom CTA'
              });
            }}
            className="px-12 py-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition font-bold text-xl shadow-lg hover:shadow-xl transform hover:scale-105 mb-8"
          >
            Începe Acum - Gratis
          </button>
        </section>

        <footer className="bg-gray-900 text-gray-300 py-8">
          <div className="max-w-6xl mx-auto px-4">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="flex items-center gap-3">
                <FileText className="text-blue-400" size={24} />
                <span className="font-bold text-white">PDF Factura</span>
              </div>
              
              <div className="flex gap-6 text-sm">
                <button onClick={() => setShowTerms(true)} className="hover:text-white transition">Termeni și Condiții</button>
                <button onClick={() => setShowPrivacy(true)} className="hover:text-white transition">Confidențialitate</button>
                <a href="mailto:contact@pdffactura.org" className="hover:text-white transition">Contact</a>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-gray-700 text-sm text-gray-400 text-center">
              <p className="mb-4">
                <strong className="text-gray-300">DISCLAIMER LEGAL:</strong> PDF Factura este un instrument de generare documente PDF 
                și nu oferă consultanță fiscală, contabilă sau juridică. Verifici acuratețea tuturor datelor introduse 
                și ești responsabil pentru conformitatea cu legislația fiscală. Nu ne asumăm răspundere pentru erori în calcule, 
                modificări ale legislației fiscale, respingeri de la autorități sau pierderi financiare. 
                Pentru întrebări fiscale, consultă un contabil autorizat.
              </p>
              <p>© 2025 PDF Factura. Made in Romania 🇷🇴</p>
            </div>
          </div>
        </footer>

        <CookieBanner />
        <TermsModal />
        <PrivacyModal />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => setShowApp(false)}>
              <FileText className="text-blue-600" size={32} />
              <div>
                <h1 className="text-2xl font-bold text-gray-800">PDF Factura</h1>
                <p className="text-sm text-gray-600">Generator facturi gratuit</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500">Facturi generate astăzi: {
                JSON.parse(localStorage.getItem('pdfFactura_usage') || '{"count": 0}').count
              }/100</p>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="flex items-center justify-center gap-4">
          {[1, 2, 3].map((num) => (
            <React.Fragment key={num}>
              <div className={`flex items-center justify-center w-10 h-10 rounded-full font-bold ${
                step >= num ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                {num}
              </div>
              {num < 3 && (
                <div className={`h-1 w-16 ${step > num ? 'bg-blue-600' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>
        <div className="flex justify-center gap-20 mt-2">
          <span className={`text-sm ${step >= 1 ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>
            Firma ta
          </span>
          <span className={`text-sm ${step >= 2 ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>
            Client
          </span>
          <span className={`text-sm ${step >= 3 ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>
            Produse/Servicii
          </span>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 pb-8">
        <div className="bg-white rounded-xl shadow-lg p-8">
          {step === 1 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <Building2 className="text-blue-600" size={28} />
                <h2 className="text-2xl font-bold text-gray-800">Datele firmei tale</h2>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nume firmă *</label>
                  <input
                    type="text"
                    value={company.name}
                    onChange={(e) => setCompany({...company, name: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="SC EXEMPLU SRL"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">CUI *</label>
                  <input
                    type="text"
                    value={company.cui}
                    onChange={(e) => setCompany({...company, cui: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="RO12345678"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nr. Reg. Com.</label>
                  <input
                    type="text"
                    value={company.regCom}
                    onChange={(e) => setCompany({...company, regCom: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="J40/1234/2020"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Oraș</label>
                  <input
                    type="text"
                    value={company.city}
                    onChange={(e) => setCompany({...company, city: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="București"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Adresă</label>
                  <input
                    type="text"
                    value={company.address}
                    onChange={(e) => setCompany({...company, address: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Str. Exemplu nr. 1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Județ</label>
                  <input
                    type="text"
                    value={company.county}
                    onChange={(e) => setCompany({...company, county: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="București"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Telefon</label>
                  <input
                    type="text"
                    value={company.phone}
                    onChange={(e) => setCompany({...company, phone: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0712345678"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    value={company.email}
                    onChange={(e) => setCompany({...company, email: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="contact@firma.ro"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Bancă</label>
                  <input
                    type="text"
                    value={company.bank}
                    onChange={(e) => setCompany({...company, bank: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="BCR"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">IBAN</label>
                  <input
                    type="text"
                    value={company.iban}
                    onChange={(e) => setCompany({...company, iban: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="RO49AAAA1B31007593840000"
                  />
                </div>
              </div>

              <div className="flex justify-end pt-6">
                <button
                  onClick={nextStep}
                  className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
                >
                  Continuă →
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
  <div className="space-y-6">
    <div className="flex items-center gap-3 mb-6">
      <User className="text-blue-600" size={28} />
      <h2 className="text-2xl font-bold text-gray-800">Datele clientului</h2>
    </div>

    {/* Selector tip client */}
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
      <label className="block text-sm font-medium text-gray-700 mb-3">Tip client *</label>
      <div className="flex gap-4">
        <button
          onClick={() => setClient({...client, type: 'company', cui: ''})}
          className={`flex-1 px-6 py-3 rounded-lg font-medium transition ${
            client.type === 'company'
              ? 'bg-blue-600 text-white'
              : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
          }`}
        >
          <Building2 size={20} className="inline mr-2" />
          Firmă
        </button>
        <button
          onClick={() => setClient({...client, type: 'individual', cui: ''})}
          className={`flex-1 px-6 py-3 rounded-lg font-medium transition ${
            client.type === 'individual'
              ? 'bg-blue-600 text-white'
              : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
          }`}
        >
          <User size={20} className="inline mr-2" />
          Persoană fizică
        </button>
      </div>
    </div>

    {/* ⬇️ SECȚIUNE NOUĂ: AUTOCOMPLETE ANAF (doar pentru firme) */}
    {client.type === 'company' && (
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-2xl">🚀</span>
          <label className="block text-sm font-medium text-gray-700">
            Completare automată din registrul ANAF e-Factura
          </label>
        </div>
        <div className="flex gap-3">
          <input
            type="text"
            placeholder="Introdu CUI (ex: 12345678 sau RO12345678)"
            value={cuiSearch}
            onChange={(e) => {
              setCuiSearch(e.target.value);
              setAnafError(''); // Clear error on type
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                fetchCompanyData();
              }
            }}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            disabled={loadingANAF}
          />
          <button
            onClick={fetchCompanyData}
            disabled={loadingANAF || !cuiSearch.trim()}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition font-medium"
          >
            {loadingANAF ? (
              <>
                <span className="inline-block animate-spin mr-2">⏳</span>
                Se caută...
              </>
            ) : (
              '🔍 Caută'
            )}
          </button>
        </div>
        {anafError && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700 text-sm">⚠️ {anafError}</p>
            <p className="text-red-600 text-xs mt-1">
              Poți completa manual câmpurile de mai jos.
            </p>
          </div>
        )}
        <p className="text-xs text-gray-600 mt-2">
          💡 Tip: Introdu CUI-ul și apasă Enter sau click "Caută". Datele se completează automat!
        </p>
      </div>
    )}

    {/* Câmpuri normale (editabile DUPĂ autocomplete) */}
    <div className="grid md:grid-cols-2 gap-6">
      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {client.type === 'individual' ? 'Nume complet' : 'Nume firmă'} *
        </label>
        <input
          type="text"
          value={client.name}
          onChange={(e) => setClient({...client, name: e.target.value})}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder={client.type === 'individual' ? 'Popescu Ion' : 'SC CLIENT SRL'}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {client.type === 'individual' ? 'CNP *' : 'CUI *'}
        </label>
        <input
          type="text"
          value={client.cui}
          onChange={(e) => setClient({...client, cui: e.target.value})}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder={client.type === 'individual' ? '1234567890123' : 'RO12345678'}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Oraș</label>
        <input
          type="text"
          value={client.city}
          onChange={(e) => setClient({...client, city: e.target.value})}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="București"
        />
      </div>

      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-2">Adresă</label>
        <input
          type="text"
          value={client.address}
          onChange={(e) => setClient({...client, address: e.target.value})}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Str. Client nr. 1"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Județ</label>
        <input
          type="text"
          value={client.county}
          onChange={(e) => setClient({...client, county: e.target.value})}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="București"
        />
      </div>
    </div>

    <div className="flex justify-between pt-6">
      <button
        onClick={() => setStep(1)}
        className="px-8 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition font-medium"
      >
        ← Înapoi
      </button>
      <button
        onClick={nextStep}
        className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
      >
        Continuă →
      </button>
    </div>
  </div>
)}

          {step === 3 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-800">Detalii factură</h2>

              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Serie *</label>
                  <input
                    type="text"
                    value={invoice.series}
                    onChange={(e) => setInvoice({...invoice, series: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Număr *</label>
                  <input
                    type="text"
                    value={invoice.number}
                    onChange={(e) => setInvoice({...invoice, number: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="001"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Data emitere</label>
				  <input
					type="date"
					value={invoice.date}
					onChange={(e) => {
						const newDate = e.target.value;
						const dueDate = new Date(new Date(newDate).getTime() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
						setInvoice({...invoice, date: newDate, dueDate: dueDate});
					}}
					className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
			  
			  <div className="grid md:grid-cols-3 gap-6 mt-4">
				<div>
					<label className="block text-sm font-medium text-gray-700 mb-2">
					  Data scadență
					  <span className="text-xs text-gray-500 ml-2">(auto-calculată +30 zile)</span>
					</label>
					<input
					  type="date"
					  value={invoice.dueDate}
					  onChange={(e) => setInvoice({...invoice, dueDate: e.target.value})}
					  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
					/>
				</div>
				<div className="md:col-span-2"></div>
			</div>

              <div className="border-t pt-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">Produse/Servicii</h3>
                  <button
                    onClick={addItem}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm"
                  >
                    <Plus size={16} />
                    Adaugă produs
                  </button>
                </div>

                <div className="space-y-3">
                  {items.map((item, index) => (
                    <div key={index} className="grid md:grid-cols-12 gap-3 items-end p-4 bg-gray-50 rounded-lg">
                      <div className="md:col-span-5">
                        <label className="block text-xs font-medium text-gray-700 mb-1">Denumire</label>
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => updateItem(index, 'description', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                          placeholder="Ex: Servicii consultanță"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-xs font-medium text-gray-700 mb-1">UM</label>
                        <select
                          value={item.unit}
                          onChange={(e) => updateItem(index, 'unit', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                        >
                          <option value="buc">buc</option>
                          <option value="ore">ore</option>
                          <option value="zile">zile</option>
                          <option value="kg">kg</option>
                          <option value="m">m</option>
                          <option value="serviciu">serviciu</option>
                        </select>
                      </div>

                      <div className="md:col-span-1">
                        <label className="block text-xs font-medium text-gray-700 mb-1">Cant.</label>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                          min="0"
                          step="0.01"
                        />
                      </div>

                      <div className="md:col-span-2">
						<label className="block text-xs font-medium text-gray-700 mb-1">Preț (RON)</label>
						<input
							type="number"
							value={item.price === 0 ? '' : item.price}
							onChange={(e) => updateItem(index, 'price', parseFloat(e.target.value) || 0)}
							className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
							min="0"
							step="0.01"
							placeholder="0.00"
						/>
					   </div>

                      <div className="md:col-span-1">
                        <label className="block text-xs font-medium text-gray-700 mb-1">Valoare</label>
                        <div className="px-3 py-2 bg-gray-100 rounded-lg text-sm font-medium text-gray-700">
                          {(item.quantity * item.price).toFixed(2)}
                        </div>
                      </div>

                      <div className="md:col-span-1">
                        <button
                          onClick={() => removeItem(index)}
                          className="w-full px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition"
                          disabled={items.length === 1}
                        >
                          <Trash2 size={16} className="mx-auto" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
                <div className="flex justify-between mb-2">
                  <span className="text-gray-700">Subtotal:</span>
                  <span className="font-semibold">{calculateSubtotal().toFixed(2)} RON</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-gray-700">TVA (21%):</span>
                  <span className="font-semibold">{calculateVAT().toFixed(2)} RON</span>
                </div>
                <div className="flex justify-between text-xl font-bold text-blue-700 pt-2 border-t border-blue-300">
                  <span>TOTAL:</span>
                  <span>{calculateTotal().toFixed(2)} RON</span>
                </div>
              </div>

              <div className="flex justify-between pt-6">
                <div className="flex gap-3">
                  <button
                    onClick={() => setStep(2)}
                    className="px-8 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition font-medium"
                  >
                    ← Înapoi
                  </button>
                  <button
                    onClick={resetForm}
                    className="px-6 py-3 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition font-medium"
                  >
                    Factură nouă
                  </button>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={previewPDF}
                    className="flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition font-medium"
                  >
                    <Eye size={20} />
                    Preview
                  </button>
                  <button
                    onClick={generatePDF}
                    className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium"
                  >
                    <Download size={20} />
                    Descarcă PDF
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {success && (
        <div className="fixed bottom-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2">
          <Save size={20} />
          {success}
        </div>
      )}
      {error && (
        <div className="fixed bottom-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2">
          <AlertCircle size={20} />
          {error}
        </div>
      )}

      <CookieBanner />
      <TermsModal />
      <PrivacyModal />
    </div>
  );
};

export default App;